import _ from "lodash";
import { defineStore } from "pinia";
import { useNotifierStore } from "@/shared/store/notifier";
import { useHsabExpenseStore } from "@/employee/store/hsabExpense";
import * as storeHelper from "@/shared/storeHelper";
import * as expenseService from "@/shared/api/expenseService";
import Vue from "vue";
import {
    ExpenseEvidenceStoreState,
    FetchState,
    HsabExpenseEvidenceResponse
} from "@/shared/types";

export const initialState: ExpenseEvidenceStoreState = {
    evidenceByExpenseId: {},
    evidenceByExpenseIdFetchState: {},
    evidenceByIdFetchState: {},
};

export const useHsabExpenseEvidenceStore = defineStore("hsabExpenseEvidence", {
    state: () => (_.cloneDeep(initialState)),
    getters: {
        getExpenseEvidenceById: state => expenseId => state.evidenceByExpenseId[expenseId],
        isEvidenceForExpenseLoading: state => expenseId => state.evidenceByExpenseIdFetchState[expenseId] === FetchState.Loading,
        isEvidenceForExpenseLoaded: state => expenseId => state.evidenceByExpenseIdFetchState[expenseId] === FetchState.Loaded,
        failedToLoadForExpense: state => expenseId => state.evidenceByExpenseIdFetchState[expenseId] === FetchState.Failed,
        isEvidenceLoading: state => expenseEvidenceId => state.evidenceByIdFetchState[expenseEvidenceId] === FetchState.Loading,
        failedSubmit: state => expenseEvidenceId => state.evidenceByIdFetchState[expenseEvidenceId] === FetchState.Loading,
    },
    actions: {
        async loadEvidenceForExpense (expenseId: number) {
            if (!expenseId || expenseId <= 0) {
                return Promise.resolve();
            }
            if (this.evidenceByExpenseIdFetchState[expenseId] &&
                this.evidenceByExpenseIdFetchState[expenseId] === FetchState.Loading) {
                return Promise.resolve();
            }

            Vue.set(this.evidenceByExpenseIdFetchState, expenseId, FetchState.Loading);
            return expenseService.getHsabExpenseEvidence(expenseId)
                .then(response => {
                    Vue.set(this.evidenceByExpenseId, expenseId, response.data);
                    Vue.set(this.evidenceByExpenseIdFetchState, expenseId, FetchState.Loaded);
                })
                .catch(err => {
                    const notifierStore = useNotifierStore();
                    storeHelper.handleError({ error: err, rethrow: true });
                    notifierStore.setDanger("Unable to load receipts for this expense, please refresh to try again.");
                    Vue.set(this.evidenceByExpenseIdFetchState, expenseId, FetchState.Failed);
                });
        },
        async retrySubmitting (expenseId: number, expenseEvidenceId: number) {
            if (this.evidenceByIdFetchState[expenseEvidenceId] &&
                this.evidenceByIdFetchState[expenseEvidenceId] === FetchState.Loading) {
                return Promise.resolve();
            }

            Vue.set(this.evidenceByIdFetchState, expenseEvidenceId, FetchState.Loading);
            return expenseService.retrySubmitHsabExpenseEvidence(expenseId, expenseEvidenceId)
                .then(response => {
                    const lst: HsabExpenseEvidenceResponse[] = this.evidenceByExpenseId[expenseId];
                    const index = _.findIndex(lst, x => x.hsabExpenseEvidenceId === expenseEvidenceId);

                    Vue.set(this.evidenceByExpenseId[expenseId], index, response.data);
                    Vue.set(this.evidenceByIdFetchState, expenseEvidenceId, FetchState.Loaded);

                    this.updateExpenseData(expenseId);
                })
                .catch(err => {
                    const notifierStore = useNotifierStore();
                    storeHelper.handleError({ error: err, rethrow: true });
                    notifierStore.setDanger("Unable to submit receipt, please try again.");
                    Vue.set(this.evidenceByIdFetchState, expenseEvidenceId, FetchState.Failed);
                });
        },
        async deleteExpenseEvidence (expenseId: number, expenseEvidenceId: number) {
            if (this.evidenceByIdFetchState[expenseEvidenceId] &&
                this.evidenceByIdFetchState[expenseEvidenceId] === FetchState.Loading) {
                return Promise.resolve();
            }

            Vue.set(this.evidenceByIdFetchState, expenseEvidenceId, FetchState.Loading);
            return expenseService.deleteHsabExpenseEvidence(expenseId, expenseEvidenceId)
                .then(response => {
                    const lst: HsabExpenseEvidenceResponse[] = this.evidenceByExpenseId[expenseId];
                    const index = _.findIndex(lst, x => x.hsabExpenseEvidenceId === expenseEvidenceId);

                    Vue.delete(this.evidenceByExpenseId[expenseId], index);
                    Vue.delete(this.evidenceByIdFetchState, expenseEvidenceId);

                    this.updateExpenseData(expenseId);
                })
                .catch(err => {
                    const notifierStore = useNotifierStore();
                    storeHelper.handleError({ error: err, rethrow: true });
                    notifierStore.setDanger("Unable to delete receipt, please try again.");
                    Vue.set(this.evidenceByIdFetchState, expenseEvidenceId, FetchState.Failed);
                });
        },
        async OpenAttachment (expenseId: number, expenseEvidenceId: number) {
            const newTab = window.open();
            return expenseService.getHsabExpenseEvidenceSignedS3Url(expenseId, expenseEvidenceId)
                .then(response => {
                    newTab.location.href = response.data;
                    setTimeout(() => {
                        if (newTab.location && newTab.location.href === "about:blank") {
                            newTab.close();
                        }
                    }, 2000);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                    newTab.close();
                });
        },
        async uploadFiles (expenseId: number, files: File[]) {
            return expenseService.uploadFilesToHsabExpense(expenseId, files);
        },
        async UploadFilesToSubmittedHsabExpense (expenseId: number, files: File[], skipSubmit: boolean) {
            const notifierStore = useNotifierStore();
            try {
                const uploadResult = await this.uploadFiles(expenseId, files);
                if (!skipSubmit) {
                    const submitResult = await expenseService.submitHsabExpenseEvidence(expenseId);
                }
                notifierStore.setSuccess(`File${files.length > 1 ? "s" : ""} uploaded successfully.`);
            } catch (err) {
                notifierStore.setDanger(`Failed to upload file${files.length > 1 ? "s" : ""}`);
            }
        },
        updateExpenseData (expenseId: number) {
            const expenseStore = useHsabExpenseStore();
            expenseStore.updateCachedExpenseEvidence(expenseId, this.evidenceByExpenseId[expenseId]);
        },
    }
});